import { MessageSquare, X } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "components/common/buttons/Button";
import {
  Content as DialogContent,
  Description as DialogDescription,
  Portal as DialogPortal,
  Root as DialogRoot,
  Title as DialogTitle,
} from "@radix-ui/react-dialog";

import { cn } from "utils/cn";
import { releaseRadixPointerLock } from "utils/releaseRadixPointerLock";

const HEADER_HEIGHT = "h-14";

type ScriptEditorCommentsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commentsPanel: ReactNode;
};

export const ScriptEditorCommentsDialog = ({
  open,
  onOpenChange,
  commentsPanel,
}: ScriptEditorCommentsDialogProps) => {
  const borderRail = "border-editor-border";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      releaseRadixPointerLock();
    }
    onOpenChange(nextOpen);
  };

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange} modal={false}>
      {open ? (
        <DialogPortal>
          <DialogContent
            className={cn(
              "fixed inset-y-0 right-0 z-[140] flex h-full max-h-[100dvh] w-full max-w-[20rem] flex-col overflow-hidden border-l bg-editor-panel p-0 shadow-2xl outline-none",
              borderRail,
              "translate-x-full transition-transform duration-300 ease-in-out data-[state=open]:translate-x-0"
            )}
            onCloseAutoFocus={(event) => event.preventDefault()}
          >
            <DialogDescription className="sr-only">
              Comments for this project document
            </DialogDescription>
            <div className="flex min-h-0 flex-1 flex-col">
              <div
                className={cn(
                  "flex shrink-0 items-center justify-between gap-2 border-b bg-editor-chrome/95 px-3 backdrop-blur",
                  borderRail,
                  HEADER_HEIGHT
                )}
              >
                <DialogTitle className="m-0 flex min-w-0 flex-1 items-center gap-2 text-xs font-semibold uppercase tracking-wide text-foreground">
                  <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  Comments
                </DialogTitle>
                <Button
                  type="button"
                  color="gray"
                  size="icon"
                  aria-label="Close comments"
                  onClick={() => handleOpenChange(false)}
                  className="h-8 w-8 shrink-0"
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{commentsPanel}</div>
            </div>
          </DialogContent>
        </DialogPortal>
      ) : null}
    </DialogRoot>
  );
};
